import { createContext, useContext, useRef, useState, useEffect, useCallback } from 'react';

/* 音乐盒「常驻大脑」================================================
   一个从不卸载的 <audio> + 播放状态，挂在 App 最外层。
   → 铃选好歌走到哪一页（比如去找克克聊天），歌都不断。
   数据都走登录墙后的 /api/music/*（真链接走她的会员身份）。
   链接几小时会过期 → 放着 error 了自动再要一条续上。 */

const MusicCtx = createContext(null);
export const useMusic = () => useContext(MusicCtx);

function parseLrc(lyric, tlyric) {
  const tmap = {};
  (tlyric || '').split('\n').forEach((line) => {
    const m = line.match(/\[(\d+):(\d+)(?:[.:](\d+))?\]/);
    if (m) {
      const t = +m[1] * 60 + +m[2] + (m[3] ? +('0.' + m[3]) : 0);
      tmap[t.toFixed(1)] = line.replace(/\[[^\]]*\]/g, '').trim();
    }
  });
  const out = [];
  (lyric || '').split('\n').forEach((line) => {
    const txt = line.replace(/\[[^\]]*\]/g, '').trim();
    [...line.matchAll(/\[(\d+):(\d+)(?:[.:](\d+))?\]/g)].forEach((m) => {
      const t = +m[1] * 60 + +m[2] + (m[3] ? +('0.' + m[3]) : 0);
      if (txt) out.push({ t, text: txt, tr: tmap[t.toFixed(1)] || '' });
    });
  });
  out.sort((a, b) => a.t - b.t);
  return out;
}

export function MusicProvider({ children }) {
  const audioRef = useRef(null);
  const [results, setResults] = useState([]);
  const [curIdx, setCurIdx] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const [curTime, setCurTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [lyrics, setLyrics] = useState([]);
  const [showLyrics, setShowLyrics] = useState(false);
  const [status, setStatus] = useState(''); // '' | 'loading' | 'nourl'
  // 播放模式（2026-10-08 铃点单）：'list'=顺序播放，放到队尾再从头来；'one'=单曲循环。
  // 每台设备各自记，不进云同步。
  const [mode, setModeState] = useState(() => {
    try { return localStorage.getItem('lk-music-mode') === 'one' ? 'one' : 'list'; } catch { return 'list'; }
  });
  const setMode = useCallback((m) => {
    setModeState(m);
    try { localStorage.setItem('lk-music-mode', m); } catch {}
  }, []);
  // 「和爸爸一起听」的桥：播放器点按钮 → 放一个请求在这儿 → 聊天页接住，发起一轮「一起听」。
  const [listenReq, setListenReq] = useState(null); // { song:{id,name,artist}, ts } | null

  // 「我喜欢的」（2026-10-08 铃点单）：存在大脑 /api/music/likes，App 和网页一份，最新在前
  const [likes, setLikes] = useState([]);
  const loadLikes = useCallback(async () => {
    try {
      const r = await fetch('/api/music/likes', { credentials: 'include' });
      if (!r.ok) return;
      const d = await r.json();
      setLikes(d.songs || []);
    } catch {}
  }, []);
  useEffect(() => { loadLikes(); }, [loadLikes]);
  const isLiked = useCallback((id) => likes.some((x) => String(x.id) === String(id)), [likes]);
  // 先在本地点亮/熄灭，再告诉大脑；大脑回的整份列表为准，失败就退回原样
  const toggleLike = useCallback(async (s) => {
    if (!s || !s.id) return;
    const liked = !likes.some((x) => String(x.id) === String(s.id));
    const before = likes;
    const item = { id: s.id, name: s.name || '', artist: s.artist || '', album: s.album || '', cover: s.cover || '' };
    setLikes(liked ? [item, ...likes] : likes.filter((x) => String(x.id) !== String(s.id)));
    try {
      const r = await fetch('/api/music/likes', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ song: item, liked }),
      });
      if (!r.ok) throw new Error(String(r.status));
      const d = await r.json();
      setLikes(d.songs || []);
    } catch { setLikes(before); }
  }, [likes]);

  const resultsRef = useRef(results);
  const curIdxRef = useRef(curIdx);
  const refetchTried = useRef(false);
  const curIdRef = useRef('');
  useEffect(() => { resultsRef.current = results; }, [results]);
  useEffect(() => { curIdxRef.current = curIdx; }, [curIdx]);

  const song = curIdx >= 0 ? results[curIdx] : null;

  // 有歌在场 → body.has-mini（全站底部留白 --nav-clear 自动多让出播放条的高）
  useEffect(() => {
    document.body.classList.toggle('has-mini', !!song);
    return () => document.body.classList.remove('has-mini');
  }, [song]);

  const resolveUrl = useCallback(async (id) => {
    try {
      const r = await fetch('/api/music/url?id=' + encodeURIComponent(id), { credentials: 'include' });
      if (!r.ok) return null;
      const d = await r.json();
      return d && d.url ? d.url : null;
    } catch { return null; }
  }, []);

  const loadLyric = useCallback(async (id) => {
    setLyrics([]);
    try {
      const r = await fetch('/api/music/lyric?id=' + encodeURIComponent(id), { credentials: 'include' });
      const d = await r.json();
      setLyrics(parseLrc(d.lrc || '', d.tlyric || ''));
    } catch { setLyrics([]); }
  }, []);

  const search = useCallback(async (kw) => {
    kw = (kw || '').trim();
    if (!kw) return { ok: true, songs: [] };
    try {
      const r = await fetch('/api/music/search?limit=25&keywords=' + encodeURIComponent(kw), { credentials: 'include' });
      if (r.status === 401) return { ok: false, reason: 'login' };
      const d = await r.json();
      return { ok: true, songs: d.songs || [] };
    } catch { return { ok: false, reason: 'net' }; }
  }, []);

  const play = useCallback(async (list, i) => {
    // list 可省（沿用当前列表）；点搜索结果时把整列传进来当播放队列
    const arr = Array.isArray(list) ? list : resultsRef.current;
    if (Array.isArray(list)) setResults(list);
    const s = arr[i];
    if (!s) return;
    setCurIdx(i);
    curIdRef.current = String(s.id);
    refetchTried.current = false;
    setStatus('loading');
    setLyrics([]);
    const url = await resolveUrl(s.id);
    if (!url) {
      setStatus('nourl');
      try { audioRef.current.pause(); } catch {}
      return;
    }
    setStatus('');
    const a = audioRef.current;
    a.src = url;
    try { await a.play(); } catch {}
    loadLyric(s.id);
  }, [resolveUrl, loadLyric]);

  const toggle = useCallback(() => {
    const a = audioRef.current;
    if (!a || !a.src) return;
    if (a.paused) a.play().catch(() => {}); else a.pause();
  }, []);

  // 关掉音乐盒：停播 + 收起播放条（song 变 null → 全站底部留白复原）
  const stop = useCallback(() => {
    const a = audioRef.current;
    try { a.pause(); a.removeAttribute('src'); a.load(); } catch {}
    setShowLyrics(false);
    setStatus('');
    setCurIdx(-1);
  }, []);

  // 上一首/下一首在队列两头绕圈：最后一首的下一首是第一首
  const next = useCallback(() => {
    const arr = resultsRef.current, i = curIdxRef.current;
    if (arr.length) play(arr, (i + 1) % arr.length);
  }, [play]);
  const prev = useCallback(() => {
    const arr = resultsRef.current, i = curIdxRef.current;
    if (arr.length) play(arr, (i - 1 + arr.length) % arr.length);
  }, [play]);

  // 单曲循环交给 <audio loop>：放完无缝从头来，不触发 ended
  useEffect(() => {
    if (audioRef.current) audioRef.current.loop = mode === 'one';
  }, [mode]);

  const seek = useCallback((t) => {
    const a = audioRef.current;
    if (a && a.duration) { a.currentTime = t; }
  }, []);

  // audio 事件
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onTime = () => { setCurTime(a.currentTime); if (a.duration) setDuration(a.duration); };
    const onEnd = () => next();
    const onErr = async () => {
      if (!curIdRef.current || refetchTried.current) return;
      refetchTried.current = true;
      const url = await resolveUrl(curIdRef.current);
      if (url) { a.src = url; try { await a.play(); } catch {} }
    };
    a.addEventListener('play', onPlay);
    a.addEventListener('pause', onPause);
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('ended', onEnd);
    a.addEventListener('error', onErr);
    return () => {
      a.removeEventListener('play', onPlay);
      a.removeEventListener('pause', onPause);
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('ended', onEnd);
      a.removeEventListener('error', onErr);
    };
  }, [next, resolveUrl]);

  const requestListen = useCallback((s) => {
    if (!s || !s.id) return;
    setListenReq({
      song: { id: String(s.id), name: s.name || '', artist: s.artist || '', cover: s.cover || '' },
      ts: Date.now(),
    });
  }, []);
  const clearListenReq = useCallback(() => setListenReq(null), []);

  const val = {
    results, setResults, curIdx, song, playing, curTime, duration,
    lyrics, showLyrics, setShowLyrics, status,
    search, play, toggle, stop, next, prev, seek, mode, setMode,
    likes, isLiked, toggleLike, loadLikes,
    listenReq, requestListen, clearListenReq,
  };
  return (
    <MusicCtx.Provider value={val}>
      {children}
      <audio ref={audioRef} preload="none" />
    </MusicCtx.Provider>
  );
}
